import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Fuse from 'fuse.js';

import { useLearnIndex, useMarketplace } from '@/lib/api/hooks';
import { Icon } from '@/lib/Icon';
import { ProductCard } from '@/features/marketplace/components/ProductCard';
import type { ArticleCard, MarketplaceProduct } from '@/types/api';

/**
 * SearchResultsScreen — /app/search?q=...
 *
 * HIPAA note: search is 100% client-side. Fuse.js indexes only the
 * already-fetched marketplace catalog (public content) and learn index
 * (public content). The patient's query string never leaves the
 * browser — no API call, no telemetry of the literal query.
 *
 * Intent classification is also client-side, via a tiny keyword
 * heuristic. It's deliberately a single helper so we can swap it for
 * a backend `/search` endpoint (LLM or rules) later without rewriting
 * the UI surface.
 */
export function SearchResultsScreen() {
  const [params] = useSearchParams();
  const query = (params.get('q') ?? '').trim();

  const { data: marketplace } = useMarketplace();
  const { data: learn } = useLearnIndex();

  const intent = useMemo(() => classifyIntent(query), [query]);

  // Fuse indexes are memoized by source data identity so they only
  // rebuild when the underlying lists change.
  const productFuse = useMemo(
    () =>
      new Fuse(marketplace?.products ?? [], {
        keys: [
          { name: 'title', weight: 2 },
          { name: 'subtitle', weight: 1 },
          { name: 'summary', weight: 1 },
          { name: 'tags', weight: 1.5 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [marketplace?.products],
  );
  const learnFuse = useMemo(
    () =>
      new Fuse(learn ?? [], {
        keys: [
          { name: 'title', weight: 2 },
          { name: 'snippet', weight: 1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [learn],
  );

  const productResults = useMemo<MarketplaceProduct[]>(
    () => (query ? productFuse.search(query).map((r) => r.item) : []),
    [productFuse, query],
  );
  const articleResults = useMemo<ArticleCard[]>(
    () => (query ? learnFuse.search(query).map((r) => r.item) : []),
    [learnFuse, query],
  );

  if (!query) {
    return (
      <div className="flex flex-col px-15 md:px-40 py-25 max-w-[65rem] mx-auto w-full">
        <p className="font-mono uppercase text-13 text-[#7C7C7C]">
          Type a search term above to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-15 md:px-40 py-25 gap-25 max-w-[65rem] mx-auto w-full">
      <p className="font-mono uppercase text-13 text-[#7C7C7C]">
        Showing results for ‘{query}’
      </p>

      {intent.kind === 'medical' && (
        <MedicalHelp articles={articleResults} />
      )}
      {intent.kind === 'account' && <AccountHelp label={intent.ctaLabel} />}
      {intent.kind === 'search' && (
        <SearchResultsBody products={productResults} articles={articleResults} />
      )}
    </div>
  );
}

// ---------- Intent classifier (client-side, no PHI leaves device) ----------

type Intent =
  | { kind: 'medical' }
  | { kind: 'account'; ctaLabel: string }
  | { kind: 'search' };

const MEDICAL_PATTERNS = [
  /\bhow\s+to\s+(take|use|inject|dose|cycle)\b/i,
  /\bshould\s+i\b/i,
  /\bis\s+it\s+safe\b/i,
  /\b(side\s*effect|interaction|contraindication)/i,
  /\b(dose|dosage|dosing)\b/i,
  /\b(shred|bulk|cut|lose\s*weight|gain\s*muscle|burn\s*fat)/i,
  /\b(symptom|diagnose|treat|treatment\s+for)\b/i,
];

const ACCOUNT_KEYWORDS: Array<{ re: RegExp; ctaLabel: string }> = [
  { re: /\baddress\b/i, ctaLabel: 'Update address' },
  { re: /\b(billing|invoice|receipt|payment|card)\b/i, ctaLabel: 'Open account' },
  { re: /\b(subscription|cancel|renewal|plan)\b/i, ctaLabel: 'Manage subscriptions' },
  { re: /\b(password|email|phone\s*number|profile|account)\b/i, ctaLabel: 'Open account' },
];

function classifyIntent(query: string): Intent {
  if (!query) return { kind: 'search' };
  if (MEDICAL_PATTERNS.some((re) => re.test(query))) return { kind: 'medical' };
  const account = ACCOUNT_KEYWORDS.find(({ re }) => re.test(query));
  if (account) return { kind: 'account', ctaLabel: account.ctaLabel };
  return { kind: 'search' };
}

// ---------- Sub-views ----------

function MedicalHelp({ articles }: { articles: ArticleCard[] }) {
  return (
    <section className="flex flex-col gap-20">
      <h2 className="type-headline-lg">Need help?</h2>
      <div className="relative indent-15 type-body-md text-[#7C7C7C] max-w-prose">
        <span aria-hidden className="absolute top-4 left-0 text-[#A9A9A9]">
          <Icon name="Arrow" viewBox="0 0 10 12" className="w-[.8rem]" />
        </span>
        It looks like you’re asking about medical advice. If you’d like to
        get help with this question, please message your clinician who
        will be able to best answer your question.
      </div>
      <Link
        to="/app/clinic"
        className="w-full h-50 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] flex items-center justify-between px-15 type-body-md hover:opacity-90"
      >
        <span>Message your clinician</span>
        <span aria-hidden className="inline-flex w-8">
          <Icon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </Link>
      {/* Surface matching learn content under the CTA — patient might
          self-serve from a guide before opening a clinician thread. */}
      {articles.length > 0 && <ResourceList articles={articles} />}
    </section>
  );
}

function AccountHelp({ label }: { label: string }) {
  return (
    <section className="flex flex-col gap-20">
      <h2 className="type-headline-lg">Need help?</h2>
      <div className="relative indent-15 type-body-md text-[#7C7C7C] max-w-prose">
        <span aria-hidden className="absolute top-4 left-0 text-[#A9A9A9]">
          <Icon name="Arrow" viewBox="0 0 10 12" className="w-[.8rem]" />
        </span>
        It looks like you’re asking about something you can manage from
        your account. You can do so by navigating to the account section
        of your portal via{' '}
        <span className="font-mono uppercase">
          menu &gt; <span className="underline">account</span>
        </span>
        .
      </div>
      <Link
        to="/app/account"
        className="w-full h-50 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] flex items-center justify-between px-15 type-body-md hover:opacity-90"
      >
        <span>{label}</span>
        <span aria-hidden className="inline-flex w-8">
          <Icon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </Link>
    </section>
  );
}

function SearchResultsBody({
  products,
  articles,
}: {
  products: MarketplaceProduct[];
  articles: ArticleCard[];
}) {
  if (products.length === 0 && articles.length === 0) {
    return (
      <p className="type-body-md text-[#7C7C7C]">
        No matches. Try a different search term, or message your clinician
        from the{' '}
        <Link to="/app/clinic" className="underline underline-offset-4">
          Clinic
        </Link>{' '}
        tab.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-30">
      {products.length > 0 && (
        <section className="flex flex-col gap-15">
          <h2 className="font-mono uppercase text-13 text-[#7C7C7C]">
            Treatments
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-20">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {articles.length > 0 && <ResourceList articles={articles} />}
    </div>
  );
}

function ResourceList({ articles }: { articles: ArticleCard[] }) {
  return (
    <section className="flex flex-col gap-15">
      <h2 className="font-mono uppercase text-13 text-[#7C7C7C]">Resources</h2>
      <ul className="flex flex-col gap-10">
        {articles.map((a) => (
          <li key={a.slug}>
            <Link
              to={
                a.kind === 'guide'
                  ? `/app/learn/${a.slug}`
                  : `/app/learn/article/${a.slug}`
              }
              className="block hover:opacity-90"
            >
              <div className="flex items-stretch border border-[#ECECEC] bg-white">
                <img
                  src="/brand/clinic-scene.png"
                  alt=""
                  aria-hidden
                  className="w-[6rem] shrink-0 aspect-square object-cover bg-[var(--surface-sunken)]"
                />
                <div className="flex-1 min-w-0 px-15 py-12 flex flex-col gap-5 justify-center">
                  <div className="font-mono text-15 uppercase text-black leading-110">
                    {a.title}
                  </div>
                  {a.snippet && (
                    <p className="font-body text-13 text-[#7C7C7C]">{a.snippet}</p>
                  )}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
